import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Timer as TimerIcon,
  Build as BuildIcon
} from '@mui/icons-material';
import { manufacturingAPI } from '../../services/api';
import { toast } from 'react-toastify';
import usePageTitle from '../../hooks/usePageTitle';

function OperationManagement() {
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingOperation, setEditingOperation] = useState(null);
  const [formData, setFormData] = useState({
    operation_code: '',
    name: '',
    description: '',
    setup_time_minutes: '',
    cycle_time_minutes: '',
    work_instructions: '',
    safety_notes: ''
  });

  usePageTitle(searchTerm ? `Operations: ${searchTerm}` : 'Operations');

  useEffect(() => {
    fetchOperations();
  }, []);

  const fetchOperations = async () => {
    try {
      setLoading(true);
      const response = await manufacturingAPI.operations.list();
      setOperations(response.data.results || response.data || []);
    } catch (error) {
      console.error('Error fetching operations:', error);
      toast.error('Failed to load operations');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (operation = null) => {
    if (operation) {
      setEditingOperation(operation);
      setFormData({
        operation_code: operation.operation_code,
        name: operation.name,
        description: operation.description || '',
        setup_time_minutes: operation.setup_time_minutes,
        cycle_time_minutes: operation.cycle_time_minutes,
        work_instructions: operation.work_instructions || '',
        safety_notes: operation.safety_notes || ''
      });
    } else {
      setEditingOperation(null);
      setFormData({
        operation_code: '',
        name: '',
        description: '',
        setup_time_minutes: '',
        cycle_time_minutes: '',
        work_instructions: '',
        safety_notes: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingOperation(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.operation_code || !formData.name || !formData.cycle_time_minutes) {
        toast.error('Operation code, name, and cycle time are required');
        return;
      }

      const submitData = {
        operation_code: formData.operation_code,
        name: formData.name,
        description: formData.description || '',
        setup_time_minutes: parseInt(formData.setup_time_minutes) || 0,
        cycle_time_minutes: parseFloat(formData.cycle_time_minutes) || 0,
        work_instructions: formData.work_instructions || '',
        safety_notes: formData.safety_notes || '',
        is_active: true
      };

      if (editingOperation) {
        await manufacturingAPI.operations.update(editingOperation.id, submitData);
        toast.success('Operation updated successfully');
      } else {
        await manufacturingAPI.operations.create(submitData);
        toast.success('Operation created successfully');
      }

      handleCloseDialog();
      fetchOperations();
    } catch (error) {
      console.error('Error saving operation:', error);
      if (error.response?.data?.operation_code) {
        toast.error('Operation code already exists');
      } else if (error.response?.data) {
        const errors = error.response.data;
        if (typeof errors === 'object') {
          const errorMessages = Object.entries(errors).map(([field, messages]) => 
            `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`
          ).join('\n');
          toast.error(`Validation errors:\n${errorMessages}`);
        } else {
          toast.error(errors);
        }
      } else {
        toast.error(editingOperation ? 'Failed to update operation' : 'Failed to create operation');
      }
    }
  };

  const handleDelete = async (operation) => {
    if (window.confirm(`Are you sure you want to delete ${operation.operation_code}?`)) {
      try {
        await manufacturingAPI.operations.delete(operation.id);
        toast.success('Operation deleted successfully');
        fetchOperations();
      } catch (error) {
        console.error('Error deleting operation:', error);
        toast.error('Failed to delete operation');
      }
    }
  };

  const formatTime = (minutes) => {
    if (minutes < 60) {
      return `${minutes}min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}min` : `${hours}h`;
  };

  const filteredOperations = operations.filter(operation =>
    operation.operation_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    operation.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Box className="page-header-row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Operation Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Operation
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Operations
              </Typography>
              <Typography variant="h3" color="primary">
                {operations.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Avg Setup Time
              </Typography>
              <Typography variant="h3" color="secondary">
                {operations.length > 0 
                  ? Math.round(operations.reduce((sum, op) => sum + op.setup_time_minutes, 0) / operations.length)
                  : 0
                }min
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Avg Cycle Time
              </Typography>
              <Typography variant="h3" color="warning">
                {operations.length > 0 
                  ? Math.round(operations.reduce((sum, op) => sum + parseFloat(op.cycle_time_minutes), 0) / operations.length)
                  : 0
                }min
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                With Safety Notes
              </Typography>
              <Typography variant="h3" color="success">
                {operations.filter(op => op.safety_notes && op.safety_notes.trim()).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            label="Search Operations"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
            }}
          />
        </CardContent>
      </Card>

      {/* Operations Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Operation Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Setup Time</TableCell>
                <TableCell>Cycle Time</TableCell>
                <TableCell>Safety Notes</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOperations.map((operation) => (
                <TableRow key={operation.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {operation.operation_code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{operation.name}</Typography>
                    {operation.description && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        {operation.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={<TimerIcon />}
                      label={formatTime(operation.setup_time_minutes)}
                      size="small"
                      color="secondary"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={<TimerIcon />}
                      label={formatTime(parseFloat(operation.cycle_time_minutes))}
                      size="small"
                      color="primary"
                    />
                  </TableCell>
                  <TableCell>
                    {operation.safety_notes && operation.safety_notes.trim() ? (
                      <Chip label="Has Safety Notes" color="warning" size="small" />
                    ) : (
                      <Typography variant="body2" color="textSecondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(operation)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(operation)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {filteredOperations.length === 0 && !loading && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <BuildIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography color="textSecondary" gutterBottom>
              {searchTerm ? 'No operations match your search criteria' : 'No operations found'}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{ mt: 2 }}
            >
              Add Your First Operation
            </Button>
          </Box>
        )}
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingOperation ? 'Edit Operation' : 'Add New Operation'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Operation Code *"
                name="operation_code"
                value={formData.operation_code}
                onChange={handleInputChange}
                disabled={editingOperation !== null}
                helperText={editingOperation ? "Operation code cannot be changed" : "Unique identifier (e.g., OP-001, CUT-STEEL)"}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Operation Name *"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                helperText="e.g., Cut Steel Legs, Weld Frame, Assemble Table"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={2}
                helperText="Brief description of what this operation does"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Setup Time (minutes)"
                name="setup_time_minutes"
                type="number"
                value={formData.setup_time_minutes}
                onChange={handleInputChange}
                inputProps={{ step: "1", min: "0" }}
                helperText="Time to prepare equipment/tools"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cycle Time (minutes) *"
                name="cycle_time_minutes"
                type="number"
                value={formData.cycle_time_minutes}
                onChange={handleInputChange}
                inputProps={{ step: "0.1", min: "0.1" }}
                helperText="Time to complete one piece"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Work Instructions"
                name="work_instructions"
                value={formData.work_instructions}
                onChange={handleInputChange}
                multiline
                rows={4}
                helperText="Detailed step-by-step instructions for workers"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Safety Notes"
                name="safety_notes"
                value={formData.safety_notes}
                onChange={handleInputChange}
                multiline
                rows={3}
                helperText="Important safety precautions and PPE requirements"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingOperation ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default OperationManagement;